import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Alert,
  Select,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconAlertTriangle } from "@tabler/icons-react";

const countDescendants = entity => {
  const children = Array.isArray(entity?.org_levels) ? entity.org_levels : [];
  return children.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
};

const EditOrgLevelEntityModal = ({
  opened,
  onClose,
  onSave,
  onDeactivate,
  entity,
  currentParentId = null,
  entityList = [],
  orgLevelTypes = [],
}) => {
  const currentName = entity?.name || "";
  const currentTypeId = entity?.org_level_type?.id ?? null;
  const isTopType = entity?.org_level_type?.top_org_level_type === true;
  const showTypeSelect = !!entity && !isTopType;

  const [name, setName] = useState(currentName);
  const [parentId, setParentId] = useState(
    currentParentId ? String(currentParentId) : null
  );
  const [typeId, setTypeId] = useState(
    currentTypeId ? String(currentTypeId) : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    if (opened) {
      setName(currentName);
      setParentId(currentParentId ? String(currentParentId) : null);
      setTypeId(currentTypeId ? String(currentTypeId) : null);
      setError("");
      setConfirmingDeactivate(false);
    }
  }, [opened, currentName, currentParentId, currentTypeId]);

  const parentOptions = useMemo(() => {
    const typeOrder = new Map();
    orgLevelTypes.forEach((t, idx) => {
      typeOrder.set(String(t.id), idx);
      if (t.name != null) typeOrder.set(t.name, idx);
    });
    const currentOrder =
      typeOrder.get(String(currentTypeId)) ??
      typeOrder.get(entity?.org_level_type?.name);

    return entityList
      .filter(e => e.id !== entity?.id)
      .filter(e => {
        if (currentOrder == null) return true;
        const order =
          typeOrder.get(String(e.org_level_type_id)) ??
          typeOrder.get(e.org_level_type_name);
        // Only allow parents whose type is higher (earlier in the list).
        return order != null && order < currentOrder;
      })
      .map(e => ({
        value: String(e.id),
        label: `${e.org_level_type_name}: ${e.name}`,
      }));
  }, [entityList, entity, orgLevelTypes, currentTypeId]);

  const showParentSelect = parentOptions.length > 0 && !isTopType;
  const descendantCount = useMemo(() => countDescendants(entity), [entity]);
  const hasDescendants = descendantCount > 0;
  const isTopLevel = currentParentId == null;

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (showParentSelect && !parentId) {
      setError("Parent group is required");
      return;
    }
    if (showTypeSelect && !typeId) {
      setError("Group type is required");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await onSave({
        name: name.trim(),
        orgLevelParentId: parentId ? Number(parentId) : null,
        orgLevelTypeId: showTypeSelect && typeId ? Number(typeId) : null,
      });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to update");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeactivate = async () => {
    setDeactivating(true);
    setError("");
    try {
      await onDeactivate();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to deactivate");
      setConfirmingDeactivate(false);
    } finally {
      setDeactivating(false);
    }
  };

  const handleClose = () => {
    setError("");
    setConfirmingDeactivate(false);
    onClose();
  };

  const nameUnchanged = name.trim() === currentName.trim();
  const parentUnchanged =
    (parentId ? Number(parentId) : null) === (currentParentId ?? null);
  const typeUnchanged =
    (typeId ? Number(typeId) : null) === (currentTypeId ?? null);
  const isUnchanged = nameUnchanged && parentUnchanged && typeUnchanged;
  const busy = loading || deactivating;

  const selectedType = orgLevelTypes.find(t => String(t.id) === typeId);
  const isSelectedBaseType = selectedType?.base_org_level_type === true;
  const showCombineWarning =
    showTypeSelect && isSelectedBaseType && !typeUnchanged && hasDescendants;

  return (
    <Modal opened={opened} onClose={handleClose} title="Edit Group" size="md">
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {confirmingDeactivate ? (
          <>
            <Alert
              icon={<IconAlertTriangle size={16} />}
              color="red"
              variant="light"
              title="Deactivate this group?"
            >
              <Text size="sm">
                Are you sure you want to deactivate{" "}
                <strong>{currentName}</strong>?
              </Text>
              {hasDescendants && (
                <Text size="sm" mt="xs" fw={600}>
                  Warning: this will also deactivate the {descendantCount} group
                  {descendantCount === 1 ? "" : "s"} underneath it.
                </Text>
              )}
            </Alert>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={() => setConfirmingDeactivate(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleConfirmDeactivate}
                loading={deactivating}
              >
                Deactivate
              </Button>
            </Group>
          </>
        ) : (
          <>
            <TextInput
              label="Name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
            {showParentSelect && (
              <Select
                label="Parent Group"
                placeholder="Select parent group"
                data={parentOptions}
                value={parentId}
                onChange={setParentId}
                searchable
                required
              />
            )}
            {showTypeSelect && (
              <Select
                label="Group Type"
                placeholder="Select group type"
                data={orgLevelTypes.map(t => ({
                  value: String(t.id),
                  label: t.name,
                }))}
                value={typeId}
                onChange={setTypeId}
                required
              />
            )}
            {showCombineWarning && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="orange"
                variant="light"
              >
                This will combine all sub groups into one {selectedType.name}.
                This cannot be undone.
              </Alert>
            )}
            <Group justify="space-between" gap="sm">
              {onDeactivate && !isTopLevel ? (
                <Button
                  color="red"
                  variant="outline"
                  onClick={() => setConfirmingDeactivate(true)}
                  disabled={busy}
                >
                  Deactivate
                </Button>
              ) : (
                <span />
              )}
              <Group gap="sm">
                <Button variant="outline" onClick={handleClose} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  loading={loading}
                  disabled={!name.trim() || isUnchanged || busy}
                >
                  Save
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default EditOrgLevelEntityModal;
